import { AaasAppSelectors } from '@va/aaas/shared/selectors';
import { DashboardType } from '@va/aaas/shared/types';
import { FEATURE_TOUR_IDS, LAYOUT_PORTAL_ID } from '@va/constants';
import { isSidebarOpen, isTabletDevice } from '@va/dashboard/selectors/ui';
import {
  TOP_BAR_TITLE_WRAPPER_ID,
  TopBarProps,
  UserInfo,
  UserMenu,
  UserMenuButton,
  WebsiteMenu,
  topBarClassNames,
  useSidebarToggleEffect,
  useUserInformation,
} from '@va/dashboard/shared/ui-layout';
import { navigationActions } from '@va/dashboard/slices';
import { CogIcon } from '@va/icons';
import { useTranslate } from '@va/localization';
import { CloseIconWithBorder, HamburgerIcon } from '@va/svg-visa-icons';
import { FeatureTourButtonContainer } from '@va/ui/feature-tour';
import { Portal } from '@va/util/components';
import { Link } from '@va/util/router';
import { WebsiteSelector } from 'aaas/components/website-selector/WebsiteSelector';
import { getAccountLogo, isTopBarHidden } from 'aaas/selectors/appSelectors';
import classNames from 'classnames';
import { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router';

export default function TopBar({ hasBackButton }: TopBarProps) {
  const isLeftNavOpen = useSelector(isSidebarOpen);
  const { userInfo } = useUserInformation();
  const dispatch = useDispatch();
  const toggleSidebar = useCallback(() => {
    dispatch(navigationActions.toggleSidebar());
  }, [dispatch]);

  useSidebarToggleEffect(hasBackButton, isLeftNavOpen, toggleSidebar);

  const accountLogo = useSelector(getAccountLogo);
  const hideTopBar = useSelector(isTopBarHidden);
  const isTablet = useSelector(isTabletDevice);
  const translate = useTranslate();
  const history = useHistory();
  const theme = useSelector(AaasAppSelectors.theme);

  const isAccountSettingsModuleEnabled = useSelector(AaasAppSelectors.selectIsAccountSettingsModuleEnabled);

  const dashboardType = useSelector(AaasAppSelectors.dashboardType);

  return (
    <div id='top-bar' className={classNames(topBarClassNames, { hidden: hideTopBar && !isTablet })}>
      <div className='hidden lg:block'>
        <WebsiteSelector />
      </div>
      <div className='flex lg:hidden flex-row flex-nowrap items-center overflow-hidden'>
        {!hasBackButton && (
          <div
            id='top-bar-icon-wrapper'
            onClick={toggleSidebar}
            className='w-6 h-6 flex items-center justify-center cursor-pointer'
          >
            {isLeftNavOpen ? <CloseIconWithBorder radius={12} color='#000' /> : <HamburgerIcon />}
          </div>
        )}
        <div id={TOP_BAR_TITLE_WRAPPER_ID} className={classNames('min-w-0', { 'pl-18': !hasBackButton })} />
      </div>
      {theme?.dashboardLogoEnabled && (
        <Link to={'/'} className='hidden lg:block cursor-pointer mx-auto'>
          <img className='block h-[3.125rem]' alt='TOP BAR LOGO' src={accountLogo} />
        </Link>
      )}
      <div className='flex items-center gap-8u'>
        {hideTopBar && !isTablet ? (
          <Portal
            elementSelector={`#${FEATURE_TOUR_IDS.pageHeaderContainer}`}
            mutationObserver={{ targetNode: `#${LAYOUT_PORTAL_ID}` }}
          >
            <FeatureTourButtonContainer />
          </Portal>
        ) : (
          <FeatureTourButtonContainer />
        )}

        {dashboardType === DashboardType.intpc ? (
          <UserMenu
            renderAccountSettingsButton={
              isAccountSettingsModuleEnabled
                ? (onClose) => (
                    <UserMenuButton
                      leftIcon={<CogIcon />}
                      text={translate('menu.myAccount')}
                      onClick={() => {
                        history.push('/my-account');
                        onClose();
                      }}
                    />
                  )
                : undefined
            }
            renderUserInfo={(onClose) => <UserInfo userInformation={userInfo} onClose={onClose} />}
          />
        ) : (
          <WebsiteMenu />
        )}
      </div>
    </div>
  );
}
